import { PureComponent } from 'react';
import { layer, source } from 'react-openlayers';

import { KML_FEATURE_TYPE } from '../../../featureTypes';
import EventBus from '../../../EventBus';
import { MISSION_EVENTS } from '../../../missionEvents';
import { MAP_EVENTS } from '../../../mapEvents';
import { getCurrentMission } from '../../../dataModelHelpers';
import { convertProjection4329 } from '../../../utils';
import { getSelectedFeature } from '../helpers/features';
import selectionManager from '../../../services/SelectionManager';
import PullinPointFeature from './common/PullinPointFeature';
import ParkingPointFeature from './common/ParkingPointFeature';
import StartingPointFeature from './common/StartingPointFeature';
import EndPointFeature from './common/EndPointFeature';

interface StaticPointsLayerProps {
  // Intentionally empty — static points don't depend on job type or
  // zoneMission. Kept as a typed object for future extensibility and
  // to match sibling layers' conventions.
}

interface StaticPointsLayerState {
  features: JSX.Element[];
}

const Z_INDEX_STATIC = 3;
const Z_INDEX_PULLIN = 3;
const Z_INDEX_PARKING_POINT = 3;
const Z_INDEX_STARTING_POINT = 3;
const Z_INDEX_END_POINT = 3;

/**
 * StaticPointsLayer — focused layer rendering the mission's 4 static
 * points: pullin, parking_point, starting_point, end_point.
 *
 * Reads coordinates directly from the Mission (no mission.to_feature
 * detour). Skips emission for points that are still at {lat:0, lon:0}.
 *
 * Subscribes to:
 *   - MISSION_EVENTS.CREATED_OR_DELETED (mission swap)
 *   - MAP_EVENTS.STATIC_POINTS_UPDATED (update_pullin/parking/
 *     starting/end, undo)
 *   - MAP_EVENTS.SELECTION_CHANGED (click-to-select visuals)
 *
 * NOT subscribed to MISSION_EVENTS.UPDATED — that event fires from
 * ~50 sites, none of which change static points except undo, which
 * now explicitly fires dispatchStaticPointsUpdated().
 */
export default class StaticPointsLayer extends PureComponent<StaticPointsLayerProps, StaticPointsLayerState> {
  constructor(props: StaticPointsLayerProps) {
    super(props);
    this.state = { features: [] };
    this.rebuildAll = this.rebuildAll.bind(this);
    this.onSelectionChanged = this.onSelectionChanged.bind(this);
  }

  private static STATIC_FEATURE_TYPES = new Set([
    KML_FEATURE_TYPE.PULLIN,
    KML_FEATURE_TYPE.PARKING_POINT,
    KML_FEATURE_TYPE.STARTING_POINT,
    KML_FEATURE_TYPE.END_POINT,
  ]);

  /** Was any static point selected on the last render? */
  private prevStaticSelected = false;

  componentDidMount() {
    EventBus.on(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    EventBus.on(MAP_EVENTS.STATIC_POINTS_UPDATED, this.rebuildAll);
    EventBus.on(MAP_EVENTS.SELECTION_CHANGED, this.onSelectionChanged);
    this.rebuildAll();
  }

  componentWillUnmount() {
    EventBus.remove(MISSION_EVENTS.CREATED_OR_DELETED, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.STATIC_POINTS_UPDATED, this.rebuildAll);
    EventBus.remove(MAP_EVENTS.SELECTION_CHANGED, this.onSelectionChanged);
  }

  /** Only rebuild when a static point's selected state flipped. */
  private onSelectionChanged() {
    const { featureType } = getSelectedFeature(selectionManager.selectedFeatures);
    const isStaticSelected = StaticPointsLayer.STATIC_FEATURE_TYPES.has(featureType as any);

    if (isStaticSelected !== this.prevStaticSelected) {
      this.rebuildAll();
    }
  }

  rebuildAll() {
    console.count('StaticPointsLayer.rebuildAll');
    const mission = getCurrentMission();

    if (!mission) {
      this.setState({ features: [] });

      return;
    }

    // Read the currently-selected feature once so each static-point
    // branch can cheaply check if it matches.
    const { selectedFeature, featureType } = getSelectedFeature(selectionManager.selectedFeatures);
    const selectedName = selectedFeature?.getProperties()?.name as string | undefined;
    this.prevStaticSelected = StaticPointsLayer.STATIC_FEATURE_TYPES.has(featureType as any);

    const features: JSX.Element[] = [];

    // --- pullin ---
    if (mission.pullin_lat && mission.pullin_lon) {
      const coords = convertProjection4329([mission.pullin_lon, mission.pullin_lat]);
      const selected = featureType === KML_FEATURE_TYPE.PULLIN && selectedName === 'pullin';
      const id = `pullin-${KML_FEATURE_TYPE.PULLIN}-${coords.join(',')}`;
      features.push(
        <PullinPointFeature
          key={id}
          id={id}
          coords={coords}
          selected={selected}
          properties={{ name: 'pullin', visibility: true } as any}
          zIndex={Z_INDEX_PULLIN}
        />,
      );
    }

    // --- parking_point ---
    if (mission.parking_point?.lat && mission.parking_point?.lon) {
      const coords = convertProjection4329([mission.parking_point.lon, mission.parking_point.lat]);
      const selected = featureType === KML_FEATURE_TYPE.PARKING_POINT && selectedName === 'parking_point';
      const id = `parking_point-${KML_FEATURE_TYPE.PARKING_POINT}`;
      features.push(
        <ParkingPointFeature
          key={id}
          id={id}
          coords={coords}
          selected={selected}
          properties={{ name: 'parking_point', visibility: true } as any}
          zIndex={Z_INDEX_PARKING_POINT}
        />,
      );
    }

    // --- starting_point ---
    if (mission.starting_point?.lat && mission.starting_point?.lon) {
      const coords = convertProjection4329([mission.starting_point.lon, mission.starting_point.lat]);
      const selected = featureType === KML_FEATURE_TYPE.STARTING_POINT && selectedName === 'starting_point';
      const id = `starting_point-${KML_FEATURE_TYPE.STARTING_POINT}`;
      features.push(
        <StartingPointFeature
          key={id}
          id={id}
          coords={coords}
          selected={selected}
          properties={{ name: 'starting_point', visibility: true } as any}
          zIndex={Z_INDEX_STARTING_POINT}
        />,
      );
    }

    // --- end_point ---
    if (mission.end_point?.lat && mission.end_point?.lon) {
      const coords = convertProjection4329([mission.end_point.lon, mission.end_point.lat]);
      const selected = featureType === KML_FEATURE_TYPE.END_POINT && selectedName === 'end_point';
      const id = `end_point-${KML_FEATURE_TYPE.END_POINT}`;
      features.push(
        <EndPointFeature
          key={id}
          id={id}
          coords={coords}
          selected={selected}
          properties={{ name: 'end_point', visibility: true } as any}
          zIndex={Z_INDEX_END_POINT}
        />,
      );
    }

    this.setState({ features });
  }

  render() {
    return (
      // @ts-ignore - layer.Vector children typing
      <layer.VectorImage zIndex={Z_INDEX_STATIC}>
        {/* @ts-ignore - source.VectorSourceReact children typing */}
        <source.VectorSourceReact>{this.state.features}</source.VectorSourceReact>
      </layer.VectorImage>
    );
  }
}
